import { Injectable } from '@angular/core';
import { CareCallGuideFormCacheEntry } from 'src/app/models/care-call-guide.model';

/**
 * Session-scoped cache of in-progress Care Call Guide forms, so switching patient-detail
 * tabs (e.g. to RPM Data) and coming back does not lose the MA's progress.
 *
 * Backed by sessionStorage on purpose:
 * - it is per browser tab, so two patients open in different tabs never mix state;
 * - the key also includes the patient id and guide key, so switching patients within
 *   the same tab keeps each form separate;
 * - it survives an accidental page reload but not closing the tab.
 *
 * The cached responses hold PHI (reported vitals and symptoms),
 * so entries are dropped as soon as their lifecycle ends: on note generation (per patient +
 * guide) and, defensively, in full on sign-out via `clearAll()` — sign-out does not close
 * the tab, so without this a shared workstation would keep the prior user's data at rest.
 */
@Injectable({
  providedIn: 'root',
})
export class CareCallGuideFormCacheService {
  /** Namespace prefix for every entry; also used by `clearAll()` to find keys to purge. */
  private static readonly KEY_PREFIX = 'care-call-guide-form:';

  save(patientId: string, guideKey: string, entry: CareCallGuideFormCacheEntry): void {
    try {
      sessionStorage.setItem(this.storageKey(patientId, guideKey), JSON.stringify(entry));
    } catch {
      // Quota exceeded / storage unavailable — persistence is best-effort, never break the form.
    }
  }

  load(patientId: string, guideKey: string): CareCallGuideFormCacheEntry | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(patientId, guideKey));
      if (!raw) {
        return null;
      }
      const entry = JSON.parse(raw) as CareCallGuideFormCacheEntry;
      // Guard against a well-formed-but-wrong-shape value (e.g. tampered storage): `responses`
      // must be an object, otherwise the restore path would spread a primitive into the form.
      if (!entry || typeof entry !== 'object' || typeof entry.responses !== 'object' || entry.responses === null) {
        return null;
      }
      return entry;
    } catch {
      return null;
    }
  }

  clear(patientId: string, guideKey: string): void {
    try {
      sessionStorage.removeItem(this.storageKey(patientId, guideKey));
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  /** Purges every cached guide form in this tab. Wired to sign-out so PHI never outlives the session. */
  clearAll(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(CareCallGuideFormCacheService.KEY_PREFIX)) {
          keys.push(key);
        }
      }
      // Purge each key in its own try/catch: on this sign-out PHI-purge path a single
      // failing removeItem must not abort the loop and leave the rest of the data at rest.
      keys.forEach((key) => {
        try {
          sessionStorage.removeItem(key);
        } catch {
          // best-effort — keep purging the remaining keys
        }
      });
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  private storageKey(patientId: string, guideKey: string): string {
    return `${CareCallGuideFormCacheService.KEY_PREFIX}${patientId}:${guideKey}`;
  }
}
